import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { languageOptions, normalizeLang, isRTL } from "i18n";
import { HiOutlineChevronDown, HiOutlineCheck } from "react-icons/hi2";
import { FaGlobe } from "react-icons/fa";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom"
  );
  const [selectedLang, setSelectedLang] = useState(() => {
    const current = i18n.language?.split("-")[0] || "fa";
    return (
      languageOptions.find((lang) => lang.code === current) ||
      languageOptions[0]
    );
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rtl = isRTL();

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 280;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeLanguage = async (lang: (typeof languageOptions)[0]) => {
    const normalized = normalizeLang(lang.code);
    await i18n.changeLanguage(normalized);
    setSelectedLang(lang);
    setIsOpen(false);
  };

  const dropdownVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.92,
      y: dropdownPosition === "bottom" ? -8 : 8,
      transition: { duration: 0.15, ease: "easeOut" },
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
        staggerChildren: 0.04,
        delayChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.92,
      y: dropdownPosition === "bottom" ? -8 : 8,
      transition: { duration: 0.12, ease: "easeIn" },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, x: rtl ? 10 : -10 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group relative flex items-center gap-2 
          rounded-full border px-3 py-2 text-sm font-medium
          transition-all duration-300 sm:px-4 sm:py-2.5
          ${
            isOpen
              ? "border-blue-400/70 bg-blue-50/60 text-blue-700 shadow-md shadow-blue-100/50 dark:border-blue-400/30 dark:bg-blue-500/10 dark:text-blue-300"
              : "border-gray-200/70 bg-white/70 text-gray-700 shadow-sm hover:border-blue-300/50 hover:bg-blue-50/40 hover:shadow-md hover:shadow-blue-100/30 dark:border-white/10 dark:bg-navy-800/60 dark:text-gray-200 dark:hover:border-blue-400/20 dark:hover:bg-blue-500/5"
          }
          backdrop-blur-sm
        `}
      >
        <span className="text-base">{selectedLang.flag}</span>

        <span className="hidden sm:inline">{selectedLang.label}</span>

        <span className="hidden text-[10px] font-medium uppercase text-gray-400 dark:text-gray-500 sm:inline">
          {selectedLang.code}
        </span>

        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`
            text-gray-400 transition-colors duration-200
            group-hover:text-gray-600 
            dark:group-hover:text-gray-300
            ${isOpen ? "text-blue-500 dark:text-blue-400" : ""}
          `}
        >
          <HiOutlineChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </motion.div>
      </motion.button>

      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`
              absolute z-50 min-w-[160px] rounded-2xl
              border border-gray-200/50 bg-white/95 
              p-1.5 shadow-lg
              shadow-gray-200/30 backdrop-blur-xl
              dark:border-white/10
              dark:bg-navy-800/95 dark:shadow-none
              sm:min-w-[180px]
              ${rtl ? "right-0" : "left-0"}
              ${
                dropdownPosition === "top"
                  ? "bottom-full mb-2"
                  : "top-full mt-2"
              }
            `}
            style={{
              transformOrigin:
                dropdownPosition === "top"
                  ? rtl
                    ? "bottom right"
                    : "bottom left"
                  : rtl
                  ? "top right"
                  : "top left",
            }}
          >
            {languageOptions.map((lang) => {
              const isSelected = selectedLang.code === lang.code;

              return (
                <motion.button
                  key={lang.code}
                  variants={itemVariants}
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.15 },
                  }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => changeLanguage(lang)}
                  className={`
                    relative flex w-full items-center gap-2.5 rounded-xl
                    px-3 py-2 text-sm transition-all duration-200
                    sm:gap-3 sm:px-3.5 sm:py-2.5
                    ${
                      isSelected
                        ? "bg-gradient-to-r from-blue-50/80 to-purple-50/80 font-medium text-blue-700 shadow-sm shadow-blue-100/30 dark:from-blue-500/15 dark:to-purple-500/15 dark:text-blue-300"
                        : "text-gray-700 hover:bg-gray-50/70 dark:text-gray-300 dark:hover:bg-white/5"
                    }
                  `}
                >
                  {isSelected && (
                    <motion.div
                      layoutId="selectedBg"
                      className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20"
                      transition={{ type: "spring", duration: 0.3 }}
                    />
                  )}

                  <span className="relative z-10 text-lg leading-none">
                    {lang.flag}
                  </span>

                  <span className="relative z-10 flex-1 text-left text-sm">
                    {lang.label}
                  </span>

                  {isSelected ? (
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 20,
                      }}
                      className="relative z-10 text-blue-600 dark:text-blue-400"
                    >
                      <HiOutlineCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </motion.span>
                  ) : (
                    <span className="relative z-10 text-[10px] font-medium uppercase text-gray-400 dark:text-gray-500">
                      {lang.code}
                    </span>
                  )}
                </motion.button>
              );
            })}

            <div className="mt-1 flex items-center justify-between border-t border-gray-100/60 px-3 pt-1.5 dark:border-white/5">
              <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                <FaGlobe className="h-2.5 w-2.5" />
                <span className="xs:inline hidden">{selectedLang.label}</span>
              </span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {selectedLang.code.toUpperCase()}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
